/**
 * APIs Externas para o módulo de Motoristas
 * Integração com BrasilAPI (CEP e FIPE)
 */

// ============================================================
// TIPOS
// ============================================================

export interface AddressData {
    street: string;
    neighborhood: string;
    city: string;
    state: string;
}

export interface VehicleBrand {
    nome: string;
    valor: string;
}

export interface VehicleModel {
    nome: string;
    valor: string;
}

// ============================================================
// BUSCAR ENDEREÇO POR CEP - BrasilAPI
// ============================================================

/**
 * Busca endereço completo pelo CEP usando BrasilAPI
 * @param cep - CEP com ou sem formatação (8 dígitos)
 * @returns Objeto com dados do endereço ou null se não encontrado
 */
export async function fetchAddressByCEP(cep: string): Promise<AddressData | null> {
    try {
        // Remove formatação do CEP
        const cleanCEP = cep.replace(/\D/g, '');

        // Valida se tem 8 dígitos
        if (cleanCEP.length !== 8) {
            console.error('CEP deve ter 8 dígitos');
            return null;
        }

        const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${cleanCEP}`);

        if (!response.ok) {
            throw new Error('CEP não encontrado');
        }

        const data = await response.json();

        return {
            street: data.street || '',
            neighborhood: data.neighborhood || '',
            city: data.city || '',
            state: data.state || ''
        };
    } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        return null;
    }
}

// ============================================================
// BUSCAR MARCAS DE VEÍCULOS - BrasilAPI/FIPE
// ============================================================

/**
 * Busca todas as marcas de carros da tabela FIPE
 * @returns Array com nome e código das marcas
 */
export async function fetchVehicleBrands(): Promise<VehicleBrand[]> {
    try {
        const response = await fetch('https://brasilapi.com.br/api/fipe/marcas/v1/carros');

        if (!response.ok) {
            throw new Error('Erro ao buscar marcas');
        }

        const data = await response.json();

        // Ordena alfabeticamente pelo nome
        return data.sort((a: VehicleBrand, b: VehicleBrand) =>
            a.nome.localeCompare(b.nome, 'pt-BR')
        );
    } catch (error) {
        console.error('Erro ao buscar marcas:', error);
        return [];
    }
}

// ============================================================
// BUSCAR MODELOS POR MARCA - BrasilAPI/FIPE
// ============================================================

/**
 * Busca modelos de uma marca específica
 * @param brandCode - Código da marca na tabela FIPE
 * @returns Array com nome e código dos modelos
 */
export async function fetchVehicleModels(brandCode: string): Promise<VehicleModel[]> {
    try {
        if (!brandCode) {
            return [];
        }

        // A API FIPE da BrasilAPI usa um endpoint diferente para modelos
        // Primeiro, precisamos buscar os veículos da marca
        const response = await fetch(
            `https://brasilapi.com.br/api/fipe/veiculos/v1/carros/${brandCode}`
        );

        if (!response.ok) {
            throw new Error('Erro ao buscar modelos');
        }

        const data = await response.json();

        // Retorna apenas modelos únicos (remove duplicatas por nome)
        const uniqueModels = new Map<string, VehicleModel>();

        data.forEach((item: { modelo: string }) => {
            // Extrai apenas o nome do modelo (sem ano/versão)
            const modelName = item.modelo.split(/\d/)[0].trim();
            if (!uniqueModels.has(modelName)) {
                uniqueModels.set(modelName, {
                    nome: modelName,
                    valor: modelName
                });
            }
        });

        return Array.from(uniqueModels.values()).sort((a, b) =>
            a.nome.localeCompare(b.nome, 'pt-BR')
        );
    } catch (error) {
        console.error('Erro ao buscar modelos:', error);
        return [];
    }
}

// ============================================================
// LISTA DE CORES DE VEÍCULOS
// ============================================================

/**
 * Retorna lista padrão de cores de veículos
 * Usado para popular o select de cores
 */
export function getVehicleColors(): string[] {
    return [
        'Branco',
        'Preto',
        'Prata',
        'Cinza',
        'Vermelho',
        'Azul',
        'Verde',
        'Amarelo',
        'Laranja',
        'Marrom',
        'Bege',
        'Dourado',
        'Vinho',
        'Rosa',
        'Outro'
    ];
}

// ============================================================
// LISTA DE ESTADOS BRASILEIROS
// ============================================================

/**
 * Retorna lista de UFs brasileiras
 */
export function getBrazilianStates(): { code: string; name: string }[] {
    return [
        { code: 'AC', name: 'Acre' },
        { code: 'AL', name: 'Alagoas' },
        { code: 'AP', name: 'Amapá' },
        { code: 'AM', name: 'Amazonas' },
        { code: 'BA', name: 'Bahia' },
        { code: 'CE', name: 'Ceará' },
        { code: 'DF', name: 'Distrito Federal' },
        { code: 'ES', name: 'Espírito Santo' },
        { code: 'GO', name: 'Goiás' },
        { code: 'MA', name: 'Maranhão' },
        { code: 'MT', name: 'Mato Grosso' },
        { code: 'MS', name: 'Mato Grosso do Sul' },
        { code: 'MG', name: 'Minas Gerais' },
        { code: 'PA', name: 'Pará' },
        { code: 'PB', name: 'Paraíba' },
        { code: 'PR', name: 'Paraná' },
        { code: 'PE', name: 'Pernambuco' },
        { code: 'PI', name: 'Piauí' },
        { code: 'RJ', name: 'Rio de Janeiro' },
        { code: 'RN', name: 'Rio Grande do Norte' },
        { code: 'RS', name: 'Rio Grande do Sul' },
        { code: 'RO', name: 'Rondônia' },
        { code: 'RR', name: 'Roraima' },
        { code: 'SC', name: 'Santa Catarina' },
        { code: 'SP', name: 'São Paulo' },
        { code: 'SE', name: 'Sergipe' },
        { code: 'TO', name: 'Tocantins' }
    ];
}
